import React, { useState, useEffect } from 'react';
import { Breadcrumb, BreadcrumbItem } from 'reactstrap';
import { Link } from "react-router-dom";
import Container from '@material-ui/core/Container';
import Grid from "@material-ui/core/Grid";
import List from "@material-ui/core/List";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import CardActions from "@material-ui/core/CardActions";
import Chip from "@material-ui/core/Chip";
import CircularProgress from "@material-ui/core/CircularProgress";
import InputAdornment from "@material-ui/core/InputAdornment";
import SearchIcon from '@material-ui/icons/Search';
import PictureAsPdfIcon from '@material-ui/icons/PictureAsPdf';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import { Typography, TextField, Button } from '@mui/material';
import { Helmet } from "react-helmet";
import CssBaseline from "@material-ui/core/CssBaseline";
import { makeStyles } from "@material-ui/core/styles";
import wallPaper from "../assets/static/images/kea-apply-wall-paper-4.png";
import keaLogo from '../assets/static/images/king-edward-zim-logo.png';
import credentials from "../config/credentials";

const useStyles = makeStyles((theme) => ({
  heroContent: {
    padding: theme.spacing(14, 0, 6),
    backgroundImage: `url(${wallPaper})`,
    backgroundColor: 'rgba(0, 0, 0, 0.68)',
    backgroundBlendMode: 'darken',
    backgroundSize: 'cover'
  },
  introBox: {
    backgroundColor: '#f5f9fc',
    padding: theme.spacing(4),
    marginBottom: theme.spacing(4),
    textAlign: 'center',
    borderRadius: '4px'
  },
  logo: {
    maxHeight: '75px',
    marginBottom: theme.spacing(2)
  },
  searchBox: {
    marginBottom: theme.spacing(4)
  },
  policyCard: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    borderTop: '4px solid rgb(245, 195, 0)',
    transition: '0.2s ease-in-out',
    '&:hover': {
      transform: 'translateY(-3px)',
      boxShadow: '0 8px 24px rgba(0,0,0,0.12)'
    }
  },
  policyCardContent: {
    flexGrow: 1
  },
  pdfIconWrap: {
    width: '48px',
    height: '48px',
    borderRadius: '50%',
    backgroundColor: '#fff7d6',
    color: '#b8860b',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: theme.spacing(2)
  },
  chip: {
    marginBottom: theme.spacing(2),
    backgroundColor: '#e8f5e9',
    color: '#2e7d32',
    fontWeight: 'bold'
  },
  cardActions: {
    padding: theme.spacing(2)
  },
  emptyState: {
    textAlign: 'center',
    padding: theme.spacing(6),
    backgroundColor: '#f5f9fc'
  }
}));


const POLICY_DESCRIPTIONS = {
  'Admissions Policy': 'Our admissions process, entry requirements, and enrolment expectations.',
  'Attendance Policy': 'Expectations around attendance, punctuality, absence reporting, and support.',
  'Behaviour Policy': 'Standards of behaviour, discipline, rewards, sanctions, and school culture.',
  'Safeguarding Policy': 'How we keep pupils safe and respond to safeguarding concerns.'
};

const POLICY_CATEGORIES = {
  'Admissions Policy': 'Admissions',
  'Attendance Policy': 'Attendance',
  'Behaviour Policy': 'Student Life',
  'Safeguarding Policy': 'Safeguarding'
};

function formatDate(value) {
  if (!value) return 'Not specified';

  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  }).format(new Date(value));
}

export default function Policies(props) {
  const classes = useStyles();

  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [openingPolicyId, setOpeningPolicyId] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    loadPolicies();
  }, []);

  const loadPolicies = () => {
    setLoading(true);

    const apiURL = 'https://www.comfycraft.co.uk/api/kea-policies/';

    fetch(apiURL)
      .then(res => {
        if (!res.ok) {
          throw new Error('Unable to load policies');
        }
        return res.json();
      })
      .then(data => {
        const mappedPolicies = data.map(policy => {
          const title = policy.title || policy.file_name?.replace('.pdf', '') || 'Policy';

          return {
            ...policy,
            title,
            category: policy.category || POLICY_CATEGORIES[title] || 'School Policy',
            description: policy.description || POLICY_DESCRIPTIONS[title] || 'Click below to view this school policy document.',
            updated_at: policy.updated_at || policy.modifiedTime
          };
        });

        setPolicies(mappedPolicies);
      })
      .catch(error => {
        console.error("Policy loading error:", error);
        setPolicies([]);
      })
      .finally(() => setLoading(false));
    };

  const handleOpenPolicy = (policy) => {
    setOpeningPolicyId(policy.id);

    const policyUrl = policy.view_url || policy.webViewLink || policy.download_url || policy.webContentLink;

    if (policyUrl) {
      window.open(policyUrl, '_blank', 'noopener,noreferrer');
    } else {
      console.error("No policy URL found for:", policy);
    }

    setOpeningPolicyId(null);
  };

  const filteredPolicies = policies.filter(policy => {
    const term = searchTerm.trim().toLowerCase();

    if (!term) return true;

    return (
      (policy.title || '').toLowerCase().includes(term) ||
      (policy.category || '').toLowerCase().includes(term) ||
      (policy.description || '').toLowerCase().includes(term)
    );
  });

  return (
    <React.Fragment>
      <Helmet>
        <meta name="description" content="King Edwards Academy policies and governance documents" />
        <title>King Edwards Academy - Policies</title>
      </Helmet>
      <CssBaseline />

      <main>
        <div className={classes.heroContent}>
          <Breadcrumb>
            <BreadcrumbItem><Link to="/">Home</Link></BreadcrumbItem>
            <BreadcrumbItem active>Policies</BreadcrumbItem>
          </Breadcrumb>
          <Container maxWidth="sm">
            <Typography component="h1" variant="h4" align="center" color="textPrimary" style={{ fontWeight: 'bold' }} gutterBottom>
              <span style={{ backgroundColor: 'rgb(245, 195, 0)', padding: '0.0em 0.2em 0.0em 0.2em' }}>Policies</span>
            </Typography>
          </Container>
        </div>
      </main>

      <div className='container'>
        <Container maxWidth="md">
          <div style={{ paddingTop: '30px', paddingBottom: '50px' }}>
            <List>
              <div className={classes.introBox}>
                <img className={classes.logo} src={keaLogo} alt="KEA Logo" />
                <Typography variant="h6" gutterBottom style={{ fontWeight: 'bold' }}>
                  School Policies
                </Typography>
                <Typography>
                  Please find below our latest school policies and governance documents. Click on any policy to view the PDF document.
                </Typography>
              </div>

              <div className={classes.searchBox}>
                <TextField
                  fullWidth
                  label="Search policies"
                  variant="outlined"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    )
                  }}
                />
              </div>

              {loading ? (
                <div style={{ textAlign: 'center', padding: '50px 0' }}>
                  <CircularProgress />
                  <Typography style={{ marginTop: '15px' }}>Loading policies...</Typography>
                </div>
              ) : filteredPolicies.length > 0 ? (
                <Grid container spacing={3}>
                  {filteredPolicies.map(policy => (
                    <Grid item xs={12} sm={6} md={4} key={policy.id}>
                      <Card className={classes.policyCard} elevation={2}>
                        <CardContent className={classes.policyCardContent}>
                          <div className={classes.pdfIconWrap}>
                            <PictureAsPdfIcon />
                          </div>

                          <Chip size="small" label={policy.category} className={classes.chip} />

                          <Typography variant="h6" gutterBottom style={{ fontWeight: 'bold' }}>
                            {policy.title}
                          </Typography>

                          <Typography variant="body2" color="textSecondary" gutterBottom>
                            {policy.description}
                          </Typography>

                          <Typography variant="caption" color="textSecondary" display="block" style={{ marginTop: '15px' }}>
                            Last updated: {formatDate(policy.updated_at)}
                          </Typography>
                        </CardContent>

                        <CardActions className={classes.cardActions}>
                          <Button
                            fullWidth
                            disableElevation
                            variant="contained"
                            color="primary"
                            onClick={() => handleOpenPolicy(policy)}
                            disabled={openingPolicyId === policy.id}
                            endIcon={openingPolicyId === policy.id ? null : <OpenInNewIcon />}
                          >
                            {openingPolicyId === policy.id ? 'Opening...' : 'View Policy'}
                          </Button>
                        </CardActions>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <div className={classes.emptyState}>
                  <Typography variant="h6" gutterBottom>No policies found</Typography>
                  <Typography color="textSecondary">Try searching for a different policy name or category.</Typography>
                </div>
              )}
            </List>
          </div>
        </Container>
      </div>
    </React.Fragment>
  );
}
